import { useCallback, useEffect, useState } from "react";
import { ServiceCollapsibleSection } from "./ServiceCollapsibleSection";
import { inputClass, primaryBtnClass, rigLabel } from "./serviceShared";
import type { useServiceOfferingsCatalog } from "./useServiceOfferingsCatalog";
import type { useServiceDefinitionsPanel } from "./useServiceDefinitionsPanel";
import type { ServiceOffering } from "@/lib/service/types";

export function ServiceSetupAdvancedSection(props: {
  offering: ServiceOffering;
  catalog: ReturnType<typeof useServiceOfferingsCatalog>;
  definitions: ReturnType<typeof useServiceDefinitionsPanel>;
  onSaved?: () => void;
}) {
  const { offering, catalog, definitions, onSaved } = props;
  const [defaultRigId, setDefaultRigId] = useState(offering.default_rig_id ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDefaultRigId(offering.default_rig_id ?? "");
  }, [offering.default_rig_id, offering.id]);

  const saveDefaultRig = useCallback(async () => {
    setBusy(true);
    await catalog.patchOffering(offering, {
      default_rig_id: defaultRigId || null,
    });
    setBusy(false);
    onSaved?.();
  }, [catalog, defaultRigId, offering, onSaved]);

  return (
    <ServiceCollapsibleSection title="Advanced" subtitle="Optional dispatch defaults" defaultOpen={false}>
      <p className="mb-3 text-xs text-gray-500 dark:text-zinc-400">
        When set, new jobs for this service prefer this rig unless dispatch overrides it.
      </p>
      <label className="mb-3 block text-sm font-medium text-gray-800 dark:text-zinc-200">
        Default rig
        <select
          className={`${inputClass} mt-1`}
          value={defaultRigId}
          onChange={(e) => setDefaultRigId(e.target.value)}
        >
          <option value="">No default rig</option>
          {definitions.allRigs.map((rig) => (
            <option key={rig.id} value={rig.id}>
              {rigLabel(definitions.allRigs, rig.id)}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className={primaryBtnClass}
        disabled={busy || catalog.actionBusy}
        onClick={() => void saveDefaultRig()}
      >
        {busy || catalog.actionBusy ? "Saving…" : "Save default rig"}
      </button>
    </ServiceCollapsibleSection>
  );
}
